import { FundClassification } from '@/models/common/FundClassification';
import type { OpenFundRequestDtoOut } from '@/models/out/OpenFundRequestDtoOut';
import type { NewCoordinatorDtoOut } from '@/models/out/NewCoordinatorDtoOut';

/**
 * Interprets a welfare request's stored coordinators for the completion form.
 *
 * When the applicant originally chose "the supported is the coordinator", the stored coordinators list
 * starts with one entry per supported (created by merging the supported into the coordinators). This
 * helper infers that toggle and returns only the *extra* coordinators to display (excluding the
 * supported-derived ones), so the completion form mirrors the original form: with the toggle on, the
 * supported's coordinator card is collapsed/hidden. Both the form prefill and the existing-documents
 * lookup rely on this so their indices stay consistent.
 */
export interface WelfareCoordinatorsInterpretation {
  isCoordinator: boolean;
  visibleCoordinators: NewCoordinatorDtoOut[];
}

export const interpretWelfareCoordinators = (
  request: OpenFundRequestDtoOut
): WelfareCoordinatorsInterpretation => {
  const isWelfare =
    request.fundClassification === FundClassification.Welfare ||
    request.fundClassification === FundClassification.CampaignForWelfare;

  const coordinators = request.coordinators ?? [];

  if (!isWelfare) {
    return { isCoordinator: false, visibleCoordinators: coordinators };
  }

  const supportedIds = (request.supporteds ?? [])
    .map((s) => s.israeliId)
    .filter((id): id is string => !!id);

  const isCoordinator =
    supportedIds.length > 0 && coordinators.some((c) => supportedIds.includes(c.israeliId));

  const visibleCoordinators = isCoordinator
    ? coordinators.filter((c) => !supportedIds.includes(c.israeliId))
    : coordinators;

  return { isCoordinator, visibleCoordinators };
};
